import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaHistory } from 'react-icons/fa';
import { useTheme } from '../../context/ThemeContext';
import { calculate, formatNumber, CalcError } from '../../lib/calculator';
import CopyButton from './CopyButton';
import { buttonClass, mutedClass, panelClass } from '../uiStyles';

const KEYS = [
  ['C', '⌫', '%', '÷'],
  ['7', '8', '9', '×'],
  ['4', '5', '6', '−'],
  ['1', '2', '3', '+'],
  ['(', ')', '0', '.'],
  ['√', '^', 'pi', '='],
];

const OPERATOR_KEYS = new Set(['÷', '×', '−', '+', '^', '%']);
const MAX_HISTORY = 8;

const Calculator = () => {
  const { isDarkMode } = useTheme();
  const { t, i18n } = useTranslation();
  const [expression, setExpression] = useState('');
  const [history, setHistory] = useState([]);

  // Live preview: an unfinished expression simply has no preview yet.
  const preview = useMemo(() => {
    if (!expression.trim()) return { value: '', error: null };
    try {
      return { value: formatNumber(calculate(expression), i18n.language), error: null };
    } catch (error) {
      return { value: '', error: error instanceof CalcError ? error.code : 'syntax' };
    }
  }, [expression, i18n.language]);

  const evaluate = () => {
    if (!expression.trim()) return;
    try {
      const result = formatNumber(calculate(expression), i18n.language);
      setHistory((entries) => [{ expression, result }, ...entries].slice(0, MAX_HISTORY));
      setExpression(result);
    } catch {
      // The preview already shows what is wrong; keep the expression for editing.
    }
  };

  const press = (key) => {
    if (key === 'C') { setExpression(''); return; }
    if (key === '⌫') { setExpression((value) => value.slice(0, -1)); return; }
    if (key === '=') { evaluate(); return; }
    setExpression((value) => value + key);
  };

  const onKeyDown = (event) => {
    if (event.key === 'Enter') { event.preventDefault(); evaluate(); }
    if (event.key === 'Escape') setExpression('');
  };

  return (
    <div className="grid gap-4 md:grid-cols-5">
      <div className="md:col-span-3 space-y-3">
        <div className={panelClass(isDarkMode)}>
          <input
            value={expression}
            onChange={(event) => setExpression(event.target.value)}
            onKeyDown={onKeyDown}
            inputMode="text"
            aria-label={t('useful.calculator.expression')}
            placeholder="2 + 3 × 4"
            className={`w-full bg-transparent text-right text-2xl font-mono outline-none ${isDarkMode ? 'text-light' : 'text-gray-800'}`}
          />
          <div className="flex items-center justify-between gap-3 mt-2 min-h-[28px]">
            <span className={`text-xs ${mutedClass(isDarkMode)}`}>
              {t('useful.calculator.hint')}
            </span>
            {preview.error && expression.trim() ? (
              <span className="text-sm text-red-500">{t(`useful.calculator.errors.${preview.error}`)}</span>
            ) : (
              <span className={`text-xl font-bold ${isDarkMode ? 'text-primary' : 'text-green-600'}`}>
                {preview.value && `= ${preview.value}`}
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-4 gap-2">
          {KEYS.flat().map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => press(key)}
              className={`${buttonClass(isDarkMode, key === '=' ? 'primary' : 'secondary')} py-3 text-base ${OPERATOR_KEYS.has(key) ? 'font-bold' : ''}`}
            >
              {key}
            </button>
          ))}
        </div>

        <CopyButton value={preview.value} />
      </div>

      <div className={`md:col-span-2 ${panelClass(isDarkMode)}`}>
        <h4 className={`flex items-center gap-2 text-sm font-semibold mb-3 ${isDarkMode ? 'text-light' : 'text-gray-700'}`}>
          <FaHistory /> {t('useful.calculator.history')}
        </h4>
        {history.length === 0 ? (
          <p className={`text-sm ${mutedClass(isDarkMode)}`}>{t('useful.calculator.noHistory')}</p>
        ) : (
          <ul className="space-y-2">
            {history.map((entry, index) => (
              <li key={`${entry.expression}-${index}`}>
                <button
                  type="button"
                  onClick={() => setExpression(entry.expression)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${isDarkMode ? 'hover:bg-dark/60 text-light/80' : 'hover:bg-white text-gray-600'}`}
                >
                  <span className={`block font-mono text-xs ${mutedClass(isDarkMode)}`}>{entry.expression}</span>
                  <span className={`font-semibold ${isDarkMode ? 'text-primary' : 'text-green-600'}`}>= {entry.result}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {history.length > 0 && (
          <button type="button" onClick={() => setHistory([])} className={`${buttonClass(isDarkMode)} mt-3 w-full`}>
            {t('useful.clear')}
          </button>
        )}
      </div>
    </div>
  );
};

export default Calculator;
